import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, Trophy, Swords, Shield, ChevronDown, ChevronUp, Star, Clock, AlertTriangle, Award } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const RegolamentoPage = () => {
  const navigate = useNavigate();
  const [regulation, setRegulation] = useState(null);
  const [loading, setLoading] = useState(true);
  const [openSection, setOpenSection] = useState('fasce');

  useEffect(() => {
    axios.get(`${API_URL}/api/ranking/dynamic/regulation`, { withCredentials: true })
      .then(res => setRegulation(res.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const toggle = (s) => setOpenSection(openSection === s ? null : s);

  if (loading) return <div className="p-6 flex items-center justify-center min-h-screen"><div className="animate-pulse text-slate-400">Caricamento...</div></div>;

  return (
    <div className="p-4 sm:p-6 lg:p-8 max-w-2xl mx-auto animate-fadeIn" data-testid="regolamento-page">
      <div className="flex items-center gap-4 mb-6">
        <button onClick={() => navigate(-1)} className="w-10 h-10 flex items-center justify-center rounded-full bg-white border border-slate-200 hover:bg-slate-50">
          <ArrowLeft className="w-5 h-5 text-slate-600" />
        </button>
        <div>
          <h1 className="font-outfit text-2xl font-semibold text-slate-900">Regolamento Ranking</h1>
          <p className="text-slate-500 text-sm">Ranking Dinamico Ananda Sport</p>
        </div>
      </div>

      {/* Intro */}
      <div className="bg-gradient-to-br from-amber-500 to-orange-500 rounded-3xl p-6 text-white mb-6">
        <Trophy className="w-10 h-10 mb-3 opacity-80" />
        <h2 className="font-outfit text-xl font-bold mb-2">Classifica Sociale Dinamica</h2>
        <p className="text-amber-100 text-sm leading-relaxed">
          Un sistema meritocratico basato sui risultati delle sfide ufficiali. Vinci, sali. Gioca con costanza, guadagna bonus. Resta fermo, perdi punti.
        </p>
      </div>

      <div className="space-y-3">
        {/* Fasce */}
        <div className="bg-white rounded-2xl border border-slate-100 overflow-hidden">
          <button onClick={() => toggle('fasce')} data-testid="section-fasce" className="w-full flex items-center justify-between p-4 hover:bg-slate-50 transition-colors">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 bg-amber-100 rounded-xl flex items-center justify-center"><Star className="w-5 h-5 text-amber-500" /></div>
              <span className="font-semibold text-slate-900">Fasce di Livello</span>
            </div>
            {openSection === 'fasce' ? <ChevronUp className="w-5 h-5 text-slate-400" /> : <ChevronDown className="w-5 h-5 text-slate-400" />}
          </button>
          {openSection === 'fasce' && regulation && (
            <div className="px-4 pb-4 space-y-2">
              <p className="text-sm text-slate-500 mb-3">Il Maestro assegna la Fascia iniziale ad ogni giocatore.</p>
              {regulation.fasce.map(f => {
                const colors = {1: 'bg-yellow-400 text-yellow-900', 2: 'bg-sky-400 text-sky-900', 3: 'bg-emerald-400 text-emerald-900', 4: 'bg-orange-300 text-orange-900', 5: 'bg-slate-300 text-slate-700'};
                return (
                  <div key={f.fascia} className="flex items-center justify-between bg-slate-50 rounded-xl p-3">
                    <div className="flex items-center gap-3">
                      <span className={`w-8 h-8 rounded-lg flex items-center justify-center text-sm font-bold ${colors[f.fascia]}`}>{f.fascia}</span>
                      <div>
                        <span className="font-medium text-slate-900 text-sm">{f.name}</span>
                      </div>
                    </div>
                    <span className="font-bold text-slate-900">{f.points} pts</span>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Sfide */}
        <div className="bg-white rounded-2xl border border-slate-100 overflow-hidden">
          <button onClick={() => toggle('sfide')} data-testid="section-sfide" className="w-full flex items-center justify-between p-4 hover:bg-slate-50 transition-colors">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 bg-red-100 rounded-xl flex items-center justify-center"><Swords className="w-5 h-5 text-red-500" /></div>
              <span className="font-semibold text-slate-900">Regole Sfide</span>
            </div>
            {openSection === 'sfide' ? <ChevronUp className="w-5 h-5 text-slate-400" /> : <ChevronDown className="w-5 h-5 text-slate-400" />}
          </button>
          {openSection === 'sfide' && regulation && (
            <div className="px-4 pb-4 space-y-3">
              <div className="flex gap-3 items-start">
                <div className="w-6 h-6 bg-emerald-100 rounded-md flex items-center justify-center flex-shrink-0 text-emerald-600 font-bold text-xs">&#10003;</div>
                <p className="text-sm text-slate-700">Puoi sfidare giocatori della <strong>tua stessa Fascia</strong></p>
              </div>
              <div className="flex gap-3 items-start">
                <div className="w-6 h-6 bg-emerald-100 rounded-md flex items-center justify-center flex-shrink-0 text-emerald-600 font-bold text-xs">&#10003;</div>
                <p className="text-sm text-slate-700">Puoi sfidare giocatori della <strong>Fascia immediatamente superiore</strong></p>
              </div>
              <div className="flex gap-3 items-start">
                <div className="w-6 h-6 bg-amber-100 rounded-md flex items-center justify-center flex-shrink-0 text-amber-600 font-bold text-xs">&#10003;</div>
                <p className="text-sm text-slate-700">Puoi sfidare giocatori della <strong>Fascia immediatamente inferiore</strong> (max 5 per stagione)</p>
              </div>
              <div className="flex gap-3 items-start">
                <div className="w-6 h-6 bg-red-100 rounded-md flex items-center justify-center flex-shrink-0 text-red-500 font-bold text-xs">&#10007;</div>
                <p className="text-sm text-slate-700"><strong>Non</strong> è possibile sfidare giocatori con 2+ Fasce di differenza</p>
              </div>
              {regulation.rules.inferior_limit && (
                <div className="bg-amber-50 rounded-lg p-2.5 mt-2">
                  <p className="text-xs text-amber-700">{regulation.rules.inferior_limit}</p>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Punteggio */}
        <div className="bg-white rounded-2xl border border-slate-100 overflow-hidden">
          <button onClick={() => toggle('punti')} data-testid="section-punti" className="w-full flex items-center justify-between p-4 hover:bg-slate-50 transition-colors">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 bg-emerald-100 rounded-xl flex items-center justify-center"><Award className="w-5 h-5 text-emerald-500" /></div>
              <span className="font-semibold text-slate-900">Calcolo Punteggio</span>
            </div>
            {openSection === 'punti' ? <ChevronUp className="w-5 h-5 text-slate-400" /> : <ChevronDown className="w-5 h-5 text-slate-400" />}
          </button>
          {openSection === 'punti' && regulation && (
            <div className="px-4 pb-4">
              <p className="text-xs text-slate-500 mb-3">I punti si aggiornano automaticamente dopo ogni sfida.</p>
              <div className="mb-4">
                <p className="text-sm font-semibold text-emerald-600 mb-2">In caso di Vittoria</p>
                <div className="space-y-1.5">
                  {[
                    {k: 'win_vs_superior', label: 'vs Fascia Superiore', sub: '"Scalpo"'},
                    {k: 'win_vs_same', label: 'vs Stessa Fascia', sub: '"Standard"'},
                    {k: 'win_vs_inferior', label: 'vs Fascia Inferiore', sub: '"Difesa"'},
                  ].map(r => (
                    <div key={r.k} className="flex items-center justify-between bg-emerald-50 rounded-lg px-3 py-2">
                      <div>
                        <span className="text-sm text-slate-700">{r.label}</span>
                        <span className="text-xs text-slate-400 ml-1.5">{r.sub}</span>
                      </div>
                      <span className="font-bold text-emerald-600">+{regulation.scoring[r.k].points}</span>
                    </div>
                  ))}
                </div>
              </div>
              <div>
                <p className="text-sm font-semibold text-red-500 mb-2">In caso di Sconfitta</p>
                <div className="space-y-1.5">
                  {[
                    {k: 'loss_vs_superior', label: 'vs Fascia Superiore', sub: '"Coraggiosa"'},
                    {k: 'loss_vs_same', label: 'vs Stessa Fascia', sub: '"Standard"'},
                    {k: 'loss_vs_inferior', label: 'vs Fascia Inferiore', sub: '"Difesa"'},
                  ].map(r => (
                    <div key={r.k} className="flex items-center justify-between bg-red-50 rounded-lg px-3 py-2">
                      <div>
                        <span className="text-sm text-slate-700">{r.label}</span>
                        <span className="text-xs text-slate-400 ml-1.5">{r.sub}</span>
                      </div>
                      <span className="font-bold text-red-500">{regulation.scoring[r.k].points}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Bonus & Penalità */}
        <div className="bg-white rounded-2xl border border-slate-100 overflow-hidden">
          <button onClick={() => toggle('bonus')} data-testid="section-bonus" className="w-full flex items-center justify-between p-4 hover:bg-slate-50 transition-colors">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 bg-purple-100 rounded-xl flex items-center justify-center"><AlertTriangle className="w-5 h-5 text-purple-500" /></div>
              <span className="font-semibold text-slate-900">Bonus e Penalità</span>
            </div>
            {openSection === 'bonus' ? <ChevronUp className="w-5 h-5 text-slate-400" /> : <ChevronDown className="w-5 h-5 text-slate-400" />}
          </button>
          {openSection === 'bonus' && regulation && (
            <div className="px-4 pb-4 space-y-3">
              <div className="bg-emerald-50 rounded-xl p-3">
                <p className="text-sm font-semibold text-emerald-700 mb-1">Bonus Fedeltà</p>
                <p className="text-sm text-emerald-600">{regulation.rules.loyalty_bonus}</p>
              </div>
              <div className="bg-amber-50 rounded-xl p-3">
                <p className="text-sm font-semibold text-amber-700 mb-1">Bonus Imbattibilità</p>
                <p className="text-sm text-amber-600">{regulation.rules.undefeated_bonus}</p>
              </div>
              <div className="bg-red-50 rounded-xl p-3">
                <p className="text-sm font-semibold text-red-600 mb-1">Penalità Inattività</p>
                <p className="text-sm text-red-500">{regulation.rules.inactivity_penalty}</p>
              </div>
              <div className="bg-sky-50 rounded-xl p-3">
                <p className="text-sm font-semibold text-sky-600 mb-1">Promozione Automatica</p>
                <p className="text-sm text-sky-500">{regulation.rules.promotion}</p>
              </div>
            </div>
          )}
        </div>

        {/* Limiti Sfide */}
        <div className="bg-white rounded-2xl border border-slate-100 overflow-hidden">
          <button onClick={() => toggle('limiti')} data-testid="section-limiti" className="w-full flex items-center justify-between p-4 hover:bg-slate-50 transition-colors">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 bg-orange-100 rounded-xl flex items-center justify-center"><Clock className="w-5 h-5 text-orange-500" /></div>
              <span className="font-semibold text-slate-900">Limiti Sfide</span>
            </div>
            {openSection === 'limiti' ? <ChevronUp className="w-5 h-5 text-slate-400" /> : <ChevronDown className="w-5 h-5 text-slate-400" />}
          </button>
          {openSection === 'limiti' && regulation && (
            <div className="px-4 pb-4 space-y-3">
              <div className="bg-orange-50 rounded-xl p-3">
                <p className="text-sm font-semibold text-orange-700 mb-1">Limite per Avversario</p>
                <p className="text-sm text-orange-600">{regulation.rules.opponent_limit}</p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default RegolamentoPage;
